// src/pages/brand/BrandPricing.tsx
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { planService } from "../../services/planService";
import { permissionsService, type AvailablePlan } from "../../services/permissionsService";
import { BrandLoadingScreen } from "../../components/brand/BrandLoadingScreen";
import { PricingPlan, DEV_STORE_PLAN, PRICING_FOOTER_NOTE, formatRevenueFee } from "../../constants/pricing";
import { useBrand } from "../../contexts/BrandContext/hooks";
import { isSamePlan, getPlanDisplayName } from "../../utils/planUtils";
import { enterprisePlanService } from "../../services/enterprisePlanService";
import { EnterprisePlanCard, EnterpriseContactButton } from "../../components/pricing/EnterprisePlanCard";
import { EnterpriseContactModal } from "../../components/pricing/EnterpriseContactModal";

export function BrandPricing() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOTPCode] = useState('');
  const [isActivatingTrial, setIsActivatingTrial] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const { brandDetails } = useBrand();
  
  // Check if redirected from feature restriction
  const upgradeRequired = searchParams.get('upgradeRequired') === 'true';
  const restrictedFeature = searchParams.get('feature');
  const currentPlan = searchParams.get('currentPlan');
  const programLimit = searchParams.get('limit');
  const currentProgramCount = searchParams.get('currentCount');

  // Check current subscription status - following settings logic
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ["subscription"],
    queryFn: () => planService.getCurrentSubscription(),
  });

  const { data: inviteStatus, isLoading: isLoadingInvite } = useQuery({
    queryKey: ["adminInviteStatus"],
    queryFn: () => planService.getAdminInviteStatus(),
  });

  // DB-driven 30-day grace status (admin invite or landing trial)
  const { data: graceStatus } = useQuery({
    queryKey: ["thirtyDayGraceStatus"],
    queryFn: () => planService.getGraceStatus(),
  });

  const remainingTrialDays = useMemo(() => {
    if (!graceStatus?.hasThirtyDayGrace || !graceStatus.endsAt) return null;
    const now = new Date();
    const end = new Date(graceStatus.endsAt);
    const diffMs = end.getTime() - now.getTime();
    if (diffMs <= 0) return 0;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [graceStatus?.hasThirtyDayGrace, graceStatus?.endsAt]);

  // Fetch available plans from backend
  const { data: backendPlans, isLoading: isLoadingPlans } = useQuery<AvailablePlan[]>({
    queryKey: ["available-plans"],
    queryFn: () => permissionsService.getAvailablePlans(),
  });

  // Status of this store's Enterprise (custom plan) request, if any
  const { data: enterpriseRequest } = useQuery({
    queryKey: ["enterprise-plan-request"],
    queryFn: () => enterprisePlanService.getMyRequest(),
  });

  // Only consider a plan as current if subscription exists and is active
  const currentPlanName = subscription?.data?.active ? subscription.name : null;
  const currentPlanData = subscription?.data?.plan;

  // Clear query params after displaying message (optional - you can remove this if you want the message to persist)

  // Convert backend plans to pricing plans format and filter based on dev store status
  const availablePlans = useMemo((): PricingPlan[] => {
    
    // Regular stores see all active plans from backend (backend already excludes Dev Store)
    if (!backendPlans) return [];
    
    const activePlans = backendPlans.map((p, index) =>{
      let plan = p;
      if(plan.name === 'Dev Store') {
        plan = DEV_STORE_PLAN as unknown as AvailablePlan;
      }
      
      // Use plan descriptions - they are the source of truth for what to display
      // Order 0 is used as the plan description (shown below plan name) AND in features list
      // Order 1+ are also used as features in the list
      const allDescriptions = plan.descriptions || [];
      const featureDescriptions = allDescriptions
        .filter(d => d.order >= 0) // Include order 0 in features list
        .sort((a, b) => a.order - b.order)
        .map(desc => ({
          name: desc.description,
        }));

      // Included AI search quota comes from the backend plan config
      if (plan.aiSearchQuestions) {
        featureDescriptions.push({
          name: `${plan.aiSearchQuestions} Social searches per month`,
        });
      }

      return ({
      id: plan.id,
      name: plan.name,
      price: plan.price.toString(),
      revenue: formatRevenueFee(plan.commissionRate),
      isPopular: index === backendPlans.length - 1, // Mark last plan as popular
      features: featureDescriptions,
    })});

    // If user has a current plan that's not in the active plans (e.g., "Starter Old")
    // Add it to the beginning of the list so they can see their current plan
    if (currentPlanName && currentPlanData && !activePlans.some(p => p.name === currentPlanName)) {
      const currentPlanCard: PricingPlan = {
        id: currentPlanData.id,
        name: currentPlanName,
        price: currentPlanData.price.toString(),
        revenue: 'Legacy Plan',
        isPopular: false,
        features: [
          { name: 'All features included', description: 'Full platform access' },
          { name: 'Unlimited limits', description: 'No restrictions' },
          { name: 'Legacy pricing', description: 'Original commission rates' },
        ],
      };
      
      return [currentPlanCard, ...activePlans];
    }

    return activePlans;
  }, [brandDetails?.isDevStore, backendPlans, currentPlanName, currentPlanData]);

  // Enterprise is a "Custom" card next to the purchasable plans. Dev stores only
  // ever see the Dev Store plan, so it stays hidden for them — decided from the
  // plans the backend returned, since brandDetails.isDevStore is not always set.
  const isDevStore =
    brandDetails?.isDevStore ||
    backendPlans?.some((p) => p.name === 'Dev Store') ||
    false;
  const showEnterpriseTier = !isDevStore;
  const hasOpenEnterpriseRequest = enterpriseRequest?.hasOpenRequest ?? false;
  const cardCount = availablePlans.length + (showEnterpriseTier ? 1 : 0);
  const gridColsClass = isDevStore
    ? 'md:grid-cols-1 max-w-md'
    : cardCount === 1
    ? 'md:grid-cols-1 max-w-md'
    : cardCount === 2
    ? 'md:grid-cols-2 max-w-3xl'
    : cardCount === 3
    ? 'md:grid-cols-3 max-w-5xl'
    : 'md:grid-cols-4';

  const handleSubscribe = async (planName: string) => {
    if (isSamePlan(planName, currentPlanName)) {
      return;
    }

    try {
      setIsLoading(planName);
      const checkoutUrl = await planService.subscribeToPlan(planName);

      if (checkoutUrl) {
        // Store the current token in multiple places before redirecting to Shopify
        const currentToken = localStorage.getItem("brand_token") || localStorage.getItem("brand_temp_token");
        if (currentToken) {
          // Store in localStorage
          localStorage.setItem("brand_token", currentToken);
          localStorage.setItem("brand_temp_token", currentToken);
          
          // Also store in sessionStorage as backup
          sessionStorage.setItem("brand_token", currentToken);
          sessionStorage.setItem("brand_temp_token", currentToken);
          
          // Store user data as well
          const userData = localStorage.getItem("brand_user");
          if (userData) {
            sessionStorage.setItem("brand_user", userData);
          }
          
          console.log("Stored tokens before Shopify redirect:", currentToken);
        }
        window.location.href = checkoutUrl;
      } else {
        toast.error("Failed to generate checkout URL");
      }
    } catch {
      toast.error("Failed to initiate subscription. Please try again.");
    } finally {
      setIsLoading(null);
    }
  };

  const handleActivateTrial = async () => {
    if (!otpCode.trim()) {
      toast.error('Please enter an OTP code');
      return;
    }

    try {
      setIsActivatingTrial(true);
      const result = await planService.activateTrialWithOTP(otpCode.trim());
      toast.success(result.message);
      setShowOTPModal(false);
      setOTPCode('');
      // Refresh the page to show updated trial status
      window.location.reload();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to activate trial. Please check your OTP code.';
      toast.error(message);
    } finally {
      setIsActivatingTrial(false);
    }
  };

  if (isLoadingSubscription || isLoadingInvite || isLoadingPlans) {
    return <BrandLoadingScreen />;
  }

  // If user already has an active subscription and not redirected for upgrade, redirect to dashboard
  if (subscription?.data?.active && !upgradeRequired) {
    navigate("/brand/dashboard");
    return null;
  }

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-bold text-foreground mb-4">
          Subscription Plans
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Choose the perfect plan to power your affiliate marketing program
        </p>
        
        {/* Upgrade Required Message */}
        {upgradeRequired && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 border border-red-200 max-w-2xl mx-auto">
            <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <div className="text-left">
              <p className="font-semibold text-sm sm:text-base">
                Upgrade to access this feature and much more
              </p>
              <p className="text-xs sm:text-sm mt-1">
                {currentProgramCount && programLimit
                  ? `You have ${currentProgramCount} affiliate program${currentProgramCount !== '1' ? 's' : ''}, which is the limit for your current plan. Please upgrade your plan to create more programs.`
                  : restrictedFeature && currentPlan 
                    ? `The feature "${restrictedFeature.replace(/_/g, ' ')}" is not available on your ${currentPlan} plan. Please upgrade to access this feature.`
                    : "This feature is not available on your current plan. Please upgrade to access this feature."}
              </p>
            </div>
          </div>
        )}
        
        {graceStatus?.hasThirtyDayGrace && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-50 text-brand-700 border border-brand-200">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <span className="text-sm sm:text-base">
              {remainingTrialDays !== null && remainingTrialDays >= 0
                ? `Enjoy your free trial – ${remainingTrialDays} day${remainingTrialDays === 1 ? '' : 's'} remaining. No Reveshare usage charges during this period.`
                : 'Enjoy your free trial period. No Reveshare usage charges during this period.'}
            </span>
          </div>
        )}

        {/* Activate Trial with OTP Button - Only show if no active subscription and no trial */}
        {!subscription?.data?.active && !graceStatus?.hasThirtyDayGrace && (
          <div className="mt-4">
            <button
              onClick={() => setShowOTPModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-brand-600 to-blue-600 text-white hover:from-brand-700 hover:to-blue-700 transition shadow-md"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
              Activate Trial Plan
            </button>
          </div>
        )}
      </div>

      {/* Pricing Cards */}
      <div className={`grid grid-cols-1 gap-6 mx-auto ${gridColsClass}`}>
        {availablePlans.map((plan) => {
          // Use exact matching for current plan check (not normalized)
          const isCurrentPlan = plan.name === currentPlanName;
          return (
            // ...rest of the code as originally present
          <div
            key={plan.id}
            className={`relative bg-card rounded-xl border-2 p-6 transition-all duration-200 hover:shadow-lg ${
              isCurrentPlan
                ? 'border-brand-500 shadow-lg'
                : plan.isPopular 
                ? 'border-brand-500 shadow-lg' 
                : 'border-border hover:border-brand-300'
            }`}
          >
            {plan.name !== 'Dev Store' && plan.isPopular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-600 text-white text-sm font-semibold rounded-full">
                Most Popular
              </div>
            )}
            {/* Plan Header */}
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                {getPlanDisplayName(plan.name)}
              </h3>
              <div className="mb-2">
                <span className="text-3xl font-bold text-foreground">
                  ${plan.price}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-brand-600 font-medium">
                {plan.revenue}
              </p>
              {isCurrentPlan && (
                <p className="mt-2 inline-block px-3 py-1 bg-brand-100 text-brand-600 rounded-full text-sm font-semibold">
                  Current Plan
                </p>
              )}
              {plan.id === 'starter' && inviteStatus?.invitedByAdmin && (
                <p className="mt-2 text-green-600 font-semibold text-sm">Free for you for the first 30 days</p>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-2   mb-8">
              {plan.features.map((feature, index) => (
                <li key={index} className="
                flex items-start text-sm">
                  <svg
                    className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <span className="text-muted-foreground">{feature.name}</span>
                    {feature.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          );
        })}

        {showEnterpriseTier && (
          <EnterprisePlanCard
            onContact={() => setShowEnterpriseModal(true)}
            hasOpenRequest={hasOpenEnterpriseRequest}
            showCta={false}
          />
        )}
      </div>

      {/* Action Buttons Section - Moved to bottom */}
      <div className="mt-12 space-y-6">
        <div className={`grid grid-cols-1 gap-6 mx-auto ${gridColsClass}`}>
          {availablePlans.map((plan) => {
            // Use exact matching for current plan check (not normalized)
            const isCurrentPlan = plan.name === currentPlanName;
            // Use normalized matching for upgrade/downgrade logic
            const currentPlanIndex = availablePlans.findIndex(p => isSamePlan(p.name, currentPlanName));
            const targetPlanIndex = availablePlans.findIndex(p => p.name === plan.name);
            
            const canUpgrade = !currentPlanName || targetPlanIndex > currentPlanIndex;
            const canDowngrade = currentPlanName && targetPlanIndex < currentPlanIndex;
            
            return (
            <button
              key={plan.id}
              onClick={() => handleSubscribe(plan.name)}
              disabled={isLoading === plan.name || isCurrentPlan || (!canUpgrade && !canDowngrade) || isLoadingSubscription || isLoadingInvite}
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                isCurrentPlan
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : plan.isPopular
                  ? 'bg-brand-600 text-white hover:bg-brand-700 shadow-lg hover:shadow-xl'
                  : 'bg-muted text-foreground hover:bg-accent border border-border'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading === plan.name ? (
                <div className="flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Processing...
                </div>
              ) : isCurrentPlan ? (
                'Current Plan'
              ) : !canUpgrade && !canDowngrade ? (
                'Not Available'
              ) : canUpgrade ? (
                !currentPlanName ? 'Activate Plan' : `Upgrade to ${getPlanDisplayName(plan.name)}`
              ) : (
                `Downgrade to ${getPlanDisplayName(plan.name)}`
              )}
            </button>
            );
          })}

          {showEnterpriseTier && (
            <EnterpriseContactButton
              onContact={() => setShowEnterpriseModal(true)}
              hasOpenRequest={hasOpenEnterpriseRequest}
            />
          )}
        </div>

        {/* Footer Notes */}
        <div className="text-center text-sm text-muted-foreground space-y-2">
          <p>{PRICING_FOOTER_NOTE}</p>
        </div>
      </div>

      {/* Enterprise "Get in touch" form */}
      <EnterpriseContactModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
        hasOpenRequest={hasOpenEnterpriseRequest}
        defaultValues={{
          contactName:
            enterpriseRequest?.request?.contactName ||
            [brandDetails?.firstName, brandDetails?.lastName]
              .filter(Boolean)
              .join(' '),
          email: enterpriseRequest?.request?.email || brandDetails?.email,
          companyName: brandDetails?.companyName ?? undefined,
        }}
      />

      {/* OTP Activation Modal */}
      {showOTPModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isActivatingTrial && setShowOTPModal(false)} />
          <div className="relative bg-card rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Activate Trial Plan</h2>
              <button
                onClick={() => setShowOTPModal(false)}
                disabled={isActivatingTrial}
                className="text-muted-foreground hover:text-muted-foreground disabled:opacity-50"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <p className="text-muted-foreground mb-6">
              Enter the OTP code provided to activate your trial period. You'll get free access to all features during the trial.
            </p>

            <div className="space-y-4">
              <div>
                <label htmlFor="otp-code" className="block text-sm font-medium text-muted-foreground mb-2">
                  OTP Code
                </label>
                <input
                  id="otp-code"
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOTPCode(e.target.value.toUpperCase())}
                  placeholder="Enter OTP code"
                  className="w-full px-4 py-3 border-2 border-border rounded-lg text-center text-lg font-mono font-bold tracking-widest uppercase focus:border-brand-500 focus:outline-none"
                  disabled={isActivatingTrial}
                  maxLength={8}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleActivateTrial();
                    }
                  }}
                />
              </div>

              <button
                onClick={handleActivateTrial}
                disabled={isActivatingTrial || !otpCode.trim()}
                className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-brand-600 to-blue-600 text-white font-semibold hover:from-brand-700 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isActivatingTrial ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Activating...
                  </span>
                ) : (
                  'Activate Trial'
                )}
              </button>

              <button
                onClick={() => setShowOTPModal(false)}
                disabled={isActivatingTrial}
                className="w-full px-4 py-3 rounded-lg border-2 border-border text-muted-foreground font-semibold hover:bg-accent transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandPricing;